import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NotificationBell } from './notifications';
import { useVersion } from '../api/health';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: string;
  comingSoon?: boolean;
}

/**
 * Stellenausschreibungen ist noch im Aufbau und steht deshalb vorerst nur
 * Administratoren und der Kollegin offen, die es erprobt. Bewusst nur der
 * Menüeintrag: die Route bleibt erreichbar, damit der Link gezielt
 * weitergegeben werden kann, ohne dafür den Code zu ändern.
 */
const STELLENAUSSCHREIBUNGEN_PILOT_EMAIL = 'lea.kompalka@juniva.de';
const STELLENAUSSCHREIBUNGEN_HREF = '/stellenausschreibungen';

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: '🏠' },
  { name: 'Bewerber', href: '/bewerber', icon: '👤' },
  { name: 'Bedarfe', href: '/bedarfe', icon: '📋' },
  { name: 'Stellenausschreibungen', href: STELLENAUSSCHREIBUNGEN_HREF, icon: '📢' },
  { name: 'Kennenlernen', href: '/kennenlernen', icon: '🤝', comingSoon: true },
];

const stammdatenNavigation = [
  { name: 'Städte', href: '/admin/stadt', icon: '🏙️' },
];

const adminNavigation = [
  { name: 'Benutzer', href: '/users', icon: '👥' },
  { name: 'Einladungen', href: '/admin/invitations', icon: '✉️' },
  { name: 'Sitzungen', href: '/admin/sessions', icon: '🔐' },
  { name: 'System-Logs', href: '/admin/logs', icon: '📋' },
  { name: 'Einstellungen', href: '/admin/settings', icon: '⚙️' },
];

const emailNavigation = [
  { name: 'E-Mail Vorlagen', href: '/email/templates', icon: '📧' },
  { name: 'Gesendete E-Mails', href: '/email/logs', icon: '📤' },
  { name: 'Posteingang', href: '/email/incoming', icon: '📥' },
];

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isAdmin = user?.role === 'ADMIN';
  const isManager = user?.role === 'MANAGER' || isAdmin;

  // Groß-/Kleinschreibung spielt bei E-Mail-Adressen keine Rolle — wer sich mit
  // „Lea.Kompalka@…" anmeldet, ist dieselbe Person.
  const darfStellenausschreibungen =
    isAdmin ||
    user?.email?.toLowerCase() === STELLENAUSSCHREIBUNGEN_PILOT_EMAIL;

  const sichtbareNavigation = navigation.filter(
    (item) =>
      item.href !== STELLENAUSSCHREIBUNGEN_HREF || darfStellenausschreibungen,
  );
  const { data: versionInfo } = useVersion();

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b">
            <span className="text-xl font-bold text-primary-600">JunivaCRM</span>
            <button
              className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
              onClick={() => setSidebarOpen(false)}
            >
              ✕
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {sichtbareNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              if (item.comingSoon) {
                return (
                  <span
                    key={item.name}
                    title="Bald verfügbar"
                    aria-disabled="true"
                    className="flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-400 opacity-50 cursor-not-allowed"
                  >
                    <span className="mr-3">{item.icon}</span>
                    {item.name}
                  </span>
                );
              }
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="mr-3">{item.icon}</span>
                  {item.name}
                </Link>
              );
            })}

            {isManager && (
              <>
                <div className="pt-4 pb-2">
                  <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Stammdaten
                  </p>
                </div>
                {stammdatenNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-3">{item.icon}</span>
                      {item.name}
                    </Link>
                  );
                })}
              </>
            )}

            {isAdmin && (
              <>
                <div className="pt-4 pb-2">
                  <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    E-Mail
                  </p>
                </div>
                {emailNavigation.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-3">{item.icon}</span>
                      {item.name}
                    </Link>
                  );
                })}
                <div className="pt-4 pb-2">
                  <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Administration
                  </p>
                </div>
                {adminNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-3">{item.icon}</span>
                      {item.name}
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          {/* User info */}
          <div className="border-t p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.role}</p>
              </div>
              <div className="flex-shrink-0 ml-2">
                <NotificationBell />
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <Link
                to="/profile"
                className="w-full flex justify-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Profileinstellungen
              </Link>
              <button
                onClick={logout}
                className="w-full flex justify-center px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              >
                Abmelden
              </button>
            </div>
            {isAdmin && versionInfo && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-400 text-center">
                  Version {versionInfo.version}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white shadow-sm lg:hidden">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              className="p-2 text-gray-500 hover:text-gray-700"
              onClick={() => setSidebarOpen(true)}
            >
              ☰
            </button>
            <span className="text-lg font-bold text-primary-600">JunivaCRM</span>
            {user && <NotificationBell />}
          </div>
        </div>

        {/* Page content */}
        <main className="p-6">{children}</main>
      </div>
    </div>
  );
}
